import { useEffect, useRef, useState } from 'react';

interface BenefitsCtaSectionProps {
  onOpenBooking: () => void;
  onOpenDealer: () => void;
}

export default function BenefitsCtaSection({ onOpenBooking, onOpenDealer }: BenefitsCtaSectionProps) {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'user' | 'business'>('user');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const userBenefits = [
    { icon: 'ri-speed-up-line', title: '回復速度が変わる', desc: '翌日に疲労を持ち越さない。連日のトレーニングも維持できる体へ。' },
    { icon: 'ri-trophy-line', title: 'パフォーマンスが上がる', desc: '本来の力が発揮できる。記録更新への近道。' },
    { icon: 'ri-sun-line', title: '日常生活が変わる', desc: '慢性的な痛みから解放され、毎朝スッキリ起きられる生活へ。' },
    { icon: 'ri-heart-line', title: '長期的な健康を守る', desc: '放置した炎症は老化を加速する。早期ケアで10年後が変わる。' },
  ];

  const businessBenefits = [
    { icon: 'ri-bar-chart-line', title: '単価UP', desc: 'フィジオショット導入により1施術あたりの単価を大幅アップ。高付加価値メニューで収益改善。' },
    { icon: 'ri-user-add-line', title: '新規集客', desc: '「プロ御用達の最新技術」が口コミを生む。差別化された施設として注目を集める。' },
    { icon: 'ri-repeat-2-line', title: '継続収益', desc: 'リカバリー効果の実感がリピーターを生む。月次の安定収益につながる継続利用。' },
    { icon: 'ri-star-line', title: '施設の差別化', desc: '全国34施設のみ導入可能。希少性ある技術で競合との明確な差別化を実現。' },
  ];

  return (
    <section id="benefits" ref={ref} className="py-16 md:py-24 bg-[#0A0C18] relative overflow-hidden">
      {/* Blue glow */}
      <div className="absolute top-1/2 right-0 w-96 h-96 rounded-full bg-[#1E6BFF]/6 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          className={`mb-10 md:mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.25em] uppercase">Benefits</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#E8EFFF] mb-4 leading-tight">
            フィジオショットが<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">あなたの未来を変える</span>
          </h2>
        </div>

        {/* Main benefits visual */}
        <div
          className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative rounded-2xl overflow-hidden w-full h-64 md:h-[420px] border border-[#1A2545]">
            <img
              src="https://public.readdy.ai/ai/img_res/580c178f7921d5fda51d992e25a18287.jpg"
              alt="フィジオショットのベネフィット"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C18]/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1E6BFF]/10 via-transparent to-transparent" />
          </div>
          <div>
            <p className="text-[#C0CBDD] text-base md:text-lg leading-relaxed mb-6">
              違和感がない毎日がある。<br />
              全力で動ける身体がある。<br />
              それは遠い夢ではなく、<br />
              <strong className="text-[#5A9AFF]">根本からくる原因を解決した先にある現実</strong>です。
            </p>
            <div className="space-y-3">
              {[
                '朝起きた時の体のだるさが消える',
                '練習後の回復が圧倒的に早くなる',
                '慢性的な体の不調に悩まされない生活',
                'パフォーマンスが最大限に発揮できる',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] rounded-full shrink-0 shadow-[0_0_8px_rgba(30,107,255,0.4)]">
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                  <span className="text-[#C0CBDD] text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* メディア掲載 */}
        <div
          className={`mb-12 md:mb-16 transition-all duration-700 delay-150 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <span className="h-px flex-1 bg-[#1A2545]" />
            <span className="text-[#D0DDF0] text-xs font-bold tracking-[0.25em] uppercase whitespace-nowrap">Media Coverage</span>
            <span className="h-px flex-1 bg-[#1A2545]" />
          </div>
          <div className="bg-[#0D1020] border border-[#1A2545] rounded-2xl overflow-hidden hover:border-[#1E6BFF]/30 transition-all">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-52 md:h-full min-h-[260px]">
                <img
                  src="https://static.readdy.ai/image/1153be8c8a2f65c98102af48ca4fb482/1b6ec87fa200e9e3d384eeaefde5ce3b.png"
                  alt="帯広新聞掲載"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0D1020]/30 hidden md:block" />
              </div>
              <div className="p-6 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block px-3 py-1 bg-[#0A1535] border border-[#1A2F60] text-[#5A9AFF] text-xs font-bold rounded-full tracking-widest uppercase">Media</span>
                </div>
                <h3 className="text-xl md:text-2xl font-black text-[#E8EFFF] mb-3 leading-snug">
                  帯広新聞に<br />
                  <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">掲載されました</span>
                </h3>
                <p className="text-[#C0CBDD] text-sm leading-relaxed mb-5">
                  フィジオショットの取り組みが帯広新聞に取り上げられました。<br />
                  地域スポーツ選手の回復・パフォーマンス向上に貢献する<br />
                  新しいリカバリー技術として注目を集めています。
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-full">
                    <i className="ri-newspaper-line text-[#1E6BFF] text-sm" />
                  </div>
                  <span className="text-[#E8EFFF] text-sm font-semibold">帯広新聞</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instagram CTA */}
        <div
          className={`mb-12 md:mb-16 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-[#0D1020] border border-[#1A2545] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0 bg-gradient-to-br from-[#F58529]/20 via-[#DD2A7B]/20 to-[#8134AF]/20 border border-[#DD2A7B]/30">
                <i className="ri-instagram-line text-2xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] bg-clip-text text-transparent" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
              </div>
              <div>
                <p className="text-[#E8EFFF] font-bold text-base md:text-lg leading-snug">
                  Instagramで症例動画を見る
                </p>
                <p className="text-[#D0DDF0] text-sm mt-0.5">
                  実際の施術・回復過程をリアルな動画でご確認いただけます
                </p>
              </div>
            </div>
            <a
              href="https://www.instagram.com/physio.shot?igsh=MWM1MWh2ODNkcmx5Ng%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap flex items-center gap-2.5 text-white font-bold text-sm md:text-base px-7 py-3.5 rounded-full transition-all duration-200 shrink-0 cursor-pointer bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-instagram-line text-white text-lg" />
              </div>
              症例動画を見る
            </a>
          </div>
        </div>

        {/* Tab section */}
        <div
          className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center mb-6 md:mb-8">
            <div className="bg-[#0D1020] border border-[#1A2545] rounded-full p-1 flex">
              <button
                onClick={() => setActiveTab('user')}
                className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'user'
                    ? 'bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] text-white shadow-[0_0_15px_rgba(30,107,255,0.4)]'
                    : 'text-[#D0DDF0] hover:text-[#C0CBDD]'
                }`}
              >
                一般ユーザー
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'business'
                    ? 'bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] text-white shadow-[0_0_15px_rgba(30,107,255,0.4)]'
                    : 'text-[#D0DDF0] hover:text-[#C0CBDD]'
                }`}
              >
                事業者・施設向け
              </button>
            </div>
          </div>

          {activeTab === 'user' && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-8">
                {userBenefits.map((b) => (
                  <div key={b.title} className="bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-5 transition-all hover:shadow-[0_0_15px_rgba(30,107,255,0.08)] group">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] group-hover:border-[#1E6BFF]/50 rounded-lg mb-3 transition-colors">
                      <i className={`${b.icon} text-[#1E6BFF] text-xl`} />
                    </div>
                    <h4 className="text-[#E8EFFF] font-bold text-sm mb-2">{b.title}</h4>
                    <p className="text-[#D0DDF0] text-xs leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <button
                  onClick={onOpenBooking}
                  className="group inline-flex items-center gap-2 px-8 md:px-10 py-3.5 md:py-4 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] hover:from-[#3D8FFF] hover:to-[#1A5FDD] text-white font-black text-base md:text-lg rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-[0_0_30px_rgba(30,107,255,0.5)] border-t border-[#5A9AFF]/30"
                >
                  <span>施術を体験する</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-[#D0DDF0] text-xs md:text-sm mt-3">まずは1回の体験から。実感した方が続々と通っています。</p>
              </div>
            </div>
          )}

          {activeTab === 'business' && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-8">
                {businessBenefits.map((b) => (
                  <div key={b.title} className="bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-5 transition-all hover:shadow-[0_0_15px_rgba(30,107,255,0.08)] group">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] group-hover:border-[#1E6BFF]/50 rounded-lg mb-3 transition-colors">
                      <i className={`${b.icon} text-[#1E6BFF] text-xl`} />
                    </div>
                    <h4 className="text-[#E8EFFF] font-bold text-sm mb-2">{b.title}</h4>
                    <p className="text-[#D0DDF0] text-xs leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#0D1020] border border-[#1A2545] rounded-xl p-5 md:p-6 mb-8">
                <h4 className="text-[#E8EFFF] font-black text-base mb-4">導入施設の収益改善イメージ</h4>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '導入前 平均単価', value: '3,000〜5,000円' },
                    { label: '導入後 平均単価', value: '8,000〜15,000円' },
                    { label: 'リピート率', value: '80%以上' },
                  ].map((item) => (
                    <div key={item.label} className="text-center bg-[#080A14] border border-[#1A2545] rounded-lg p-3">
                      <p className="text-[#D0DDF0] text-xs mb-1">{item.label}</p>
                      <p className="text-[#5A9AFF] font-black text-sm md:text-base">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center">
                <button
                  onClick={onOpenDealer}
                  className="group inline-flex items-center gap-2 px-8 md:px-10 py-3.5 md:py-4 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] hover:from-[#3D8FFF] hover:to-[#1A5FDD] text-white font-black text-base md:text-lg rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-[0_0_30px_rgba(30,107,255,0.5)] border-t border-[#5A9AFF]/30"
                >
                  <span>無料で導入相談をする</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-[#D0DDF0] text-xs md:text-sm mt-3">まずは無料相談から。導入コストや収益シミュレーションをご提示します。</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
